import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Sidebar } from './components/layout/Sidebar';
import { ScrollProgress } from './components/ui/ScrollProgress';
import { TooltipHost } from './components/ui/TooltipHost';
import { AuditAssistant } from './components/AuditAssistant/AuditAssistant';
import { LoginPage } from './pages/LoginPage';
import { LandingPage } from './pages/LandingPage';
import { DashboardPage } from './pages/DashboardPage';
import { DDUploadPage } from './pages/DDUploadPage';
import { SPAUploadPage } from './pages/SPAUploadPage';
import { DDAssessmentsListPage } from './pages/DDAssessmentsListPage';
import { SPAReviewsListPage } from './pages/SPAReviewsListPage';
import { ProcessingPage } from './pages/ProcessingPage';
import { AssessmentPage } from './pages/AssessmentPage';
import { ReportPage } from './pages/ReportPage';
/* Sprint 5 — Project-first audit pipeline pages. */
import { NewProjectPage } from './pages/NewProjectPage';
import { ProjectWorkspacePage } from './pages/ProjectWorkspacePage';
import { AuditAIListPage } from './pages/AuditAIListPage';
import { RiskReportPage } from './pages/RiskReportPage';
import { RiskProcessingPage } from './pages/RiskProcessingPage';

function ProtectedLayout({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <span className="spinner spinner-lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-layout">
      <ScrollProgress />
      <Sidebar />
      <div className="app-main">{children}</div>
      <AuditAssistant />
      {/* One delegated tooltip for every [data-tip] in the app — replaces the
          native `title` popup and its ~1s browser delay. */}
      <TooltipHost />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public surfaces — accessible without auth. */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />

          {/* Authenticated surfaces. */}
          <Route
            path="/dashboard"
            element={
              <ProtectedLayout>
                <DashboardPage />
              </ProtectedLayout>
            }
          />
          {/* UI-SP2-6 — DD listing. */}
          <Route
            path="/dd/assessments"
            element={
              <ProtectedLayout>
                <DDAssessmentsListPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/dd/upload"
            element={
              <ProtectedLayout>
                <DDUploadPage />
              </ProtectedLayout>
            }
          />
          {/* UI-SP2-7 — SPA listing. */}
          <Route
            path="/spa/reviews"
            element={
              <ProtectedLayout>
                <SPAReviewsListPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/spa/upload"
            element={
              <ProtectedLayout>
                <SPAUploadPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/assessments/:assessmentId"
            element={
              <ProtectedLayout>
                <AssessmentPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/processing/:jobId"
            element={
              <ProtectedLayout>
                <ProcessingPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/reports/:jobId"
            element={
              <ProtectedLayout>
                <ReportPage />
              </ProtectedLayout>
            }
          />
          {/* Sprint 5 — Project lifecycle routes. */}
          <Route
            path="/projects"
            element={
              <ProtectedLayout>
                <AuditAIListPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/projects/new"
            element={
              <ProtectedLayout>
                <NewProjectPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/projects/:auditId"
            element={
              <ProtectedLayout>
                <ProjectWorkspacePage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/projects/:auditId/risk-report"
            element={
              <ProtectedLayout>
                <RiskReportPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/projects/:auditId/risk/processing"
            element={
              <ProtectedLayout>
                <RiskProcessingPage />
              </ProtectedLayout>
            }
          />
          {/* Sprint 5 — Project-scoped upload routes. Reuse the
              existing DDUploadPage / SPAUploadPage components; the
              :auditId param is available via useParams and the page
              will surface a "Uploading for project X" banner. */}
          <Route
            path="/projects/:auditId/dd/upload"
            element={
              <ProtectedLayout>
                <DDUploadPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/projects/:auditId/spa/upload"
            element={
              <ProtectedLayout>
                <SPAUploadPage />
              </ProtectedLayout>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
